import React, { useState, useEffect } from "react";
import { Container, Box, Typography } from "@mui/material";
import liff from "../../../lib/liff";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import QuestCard from "works/chi/components/QuestCard";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { listQuests, getUserEpisode } from "utils/api";
import Loading from "default/components/Loading";
import ImageWithSaveButton from "works/chi/components/ImageWithSaveButton";
import StyledButton from "works/chi/components/StyledButton";
import AppLayout from "works/chi/components/AppLayout";
import { COLORS } from "constants/colors";
import EpisodeError from "works/chi/components/EpisodeError";
import EpisodeEmpty from "works/chi/components/EpisodeEmpty";

function Quests() {
  const { t, i18n } = useTranslation(["chi"]);
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  // クエリパラメータを取得
  const soulId = queryParams.get("soul_id");
  const lng = queryParams.get("lng") || "ja";

  const [quests, setQuests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [afterPurchase, setAfterPurchase] = useState(true);
  const [imageUrls, setImageUrls] = useState([]);
  const [isError, setIsError] = useState(false);
  const [isEpisodeCleared, setIsEpisodeCleared] = useState(false);
  const [showQuestEyeCatch, setShowQuestEyeCatch] = useState(false);

  // ルートに ?lng=xx をつける
  const withLng = (path) => {
    const hasQuery = path.includes("?");
    return lng ? `${path}${hasQuery ? "&" : "?"}lng=${lng}` : path;
  };

  const getImagePieceUrls = async (quests) => {
    const imageUrls = quests
      .filter((quest) => quest.is_cleared)
      .map((quest) => quest.reward_url);

    return imageUrls;
  };

  const onEpisodeEmpty = () => {
    navigate(withLng(`/chi/tickets?soul_id=${soulId}`));
  };

  const handleCloseWindow = () => {
    liff.closeWindow();
  };

  const onRequestJudgement = () => {
    window.open(
      "https://docs.google.com/forms/d/e/1FAIpQLSckxdz-E1hLeEAwtCSYx5zoSNq6s-tDAibm7uEu3xMNDhMI8g/viewform",
      "_blank"
    );
  };

  // クエストの初回読み込み
  const initQuests = async (soulId) => {
    setIsLoading(true);

    try {
      const userEpisodeData = await getUserEpisode(soulId);

      if (userEpisodeData === null) {
        setAfterPurchase(false);
      } else {
        const questsData = await listQuests(soulId);

        if (userEpisodeData.is_episode_cleared) {
          setImageUrls([userEpisodeData.reward_url]);
          setIsEpisodeCleared(true);
        } else {
          const urls = await getImagePieceUrls(questsData);
          setImageUrls(urls);
        }
        setShowQuestEyeCatch(userEpisodeData.show_quest_eye_catch);
        setQuests(questsData);
      }
    } catch (error) {
      // 修正箇所: エピソードが購入されていない場合の処理を追加
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  // 初回ロード時
  useEffect(() => {
    initQuests(soulId);
  }, []);

  return (
    <AppLayout>
      {isLoading && <Loading />}
      {!isLoading && isError && <EpisodeError />}
      {!isLoading && !isError && !afterPurchase && (
        <EpisodeEmpty onClick={onEpisodeEmpty} />
      )}
      {!isLoading && !isError && afterPurchase && quests.length === 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center", // テキストを中央揃え
            minHeight: "100vh",
          }}
        >
          <SearchOffIcon sx={{ fontSize: "100px", color: "text.disabled" }} />
          <Typography
            variant="h6"
            sx={{
              marginTop: "16px",
              marginBottom: "16px",
              color: "text.secondary",
              fontSize: "16px",
            }}
          >
            {/* ミッションはありません */}
            {t("chi:quest.no_quests")}
          </Typography>
        </Box>
      )}
      {!isLoading && quests.length > 0 && (
        <Box
          sx={{
            display: "flex",
            paddingTop: 4, // 任意で上マージンを追加
            paddingBottom: 4, // 任意で下マージンを追加
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
            minHeight: "100vh",
          }}
        >
          <Container
            sx={{
              display: "flex",
              paddingLeft: 0,
              paddingRight: 0,
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              width: "100%",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontFamily: '"Noto Serif JP", serif',
                fontSize: "20px",
                fontWeight: "bold",
                color: COLORS.TEXT_WHITE,
              }}
            >
              Quest Reward
            </Typography>
            <ImageWithSaveButton
              imageUrls={imageUrls}
              showDownloadButton={isEpisodeCleared}
            />
          </Container>

          <Box
            sx={{
              width: "100%",
              height: "1px",
              backgroundColor: COLORS.DARK_GRAY,
              marginTop: 2,
              marginBottom: 2,
            }}
          ></Box>

          {quests.map((quest) => (
            <QuestCard
              key={quest.quest_key}
              quest={quest}
              style={{ cursor: "pointer" }}
              withEyeCatch={showQuestEyeCatch}
            />
          ))}

          {lng === "ja" && (
            <StyledButton
              variant="contained"
              sx={{
                marginTop: 4,
              }}
              onClick={onRequestJudgement}
            >
              {/* クリア判定依頼 */}
              {t("chi:button.request_clear_judgement")}
            </StyledButton>
          )}

          <StyledButton
            variant="contained"
            sx={{
              marginTop: 1,
            }}
            onClick={handleCloseWindow}
          >
            {/* チャットに戻る */}
            {t("chi:button.back_to_chat")}
          </StyledButton>
        </Box>
      )}
    </AppLayout>
  );
}

export default Quests;
